
import { Component, Vue, Prop } from 'vue-property-decorator';
import { TradeFromTzstats, ContractToken } from '@/lib/types-helper';
import { Module1Module } from '@/store/modules/module1';

@Component
export default class Nav extends Vue {
  @Prop({
    required: true
  }) readonly arrayOfDataObjects!: TradeFromTzstats[]

  @Prop({
    required: true
  }) readonly arrayOfHeaders!: string[]

  showModal = false;
  allowModalToBeClosed = false;
  modalShowCancel = false;
  modalShowAccept = true;
  modalTitle = 'Please Wait';
  modalParagraph = 'Transaction pending...'
  tradeIdToInteractWith!: number;

  getObjktURLFromTokenData (data: ContractToken) : string {
    return `https://objkt.com/collection/${data.fa2}/${data.id}`;
  }

  getUserHTMLString (address: string) : string {
    if (Module1Module.account && address === Module1Module.account.address) {
      return 'You';
    } else if (Module1Module.xtznftswapContract && address === Module1Module.xtznftswapContract.address) {
      return 'Anyone';
    } else {
      return `<a href="https://objkt.com/profile/${address}" target="_blank">${address.substring(0, 5)}...${address.slice(-5)}</a>`;
    }
  }

  getNFTHTMLString (data: ContractToken[]) : string {
    let string = '';

    data.forEach((token, idx) => {
      string += `<a href="${this.getObjktURLFromTokenData(token)}" target="_blank">NFT #${idx + 1} (x1)</a>`;
    });
    return string;
  }

  possiblyCloseModal () : void {
    if (this.allowModalToBeClosed) {
      this.showModal = false;
      this.allowModalToBeClosed = false;
    }
  }

  openModal (tradeId: string): void {
    this.tradeIdToInteractWith = parseInt(tradeId);
    this.showModal = true;
    this.allowModalToBeClosed = true;
    this.modalTitle = 'Trade Interaction';
    this.modalParagraph = `What would you like to do with trade <span class="italic font-bold">ID ${tradeId}?</span><br><br>Trade Permalink:<br><a href="https://xtznftswap.xyz/?trade=${this.tradeIdToInteractWith}#/accept" target="_blank">https://xtznftswap.xyz/?trade=${this.tradeIdToInteractWith}#/accept</a>`;

    const theTrade = this.arrayOfDataObjects.find((datum: TradeFromTzstats) => datum.key === tradeId);

    // check if account is the proposer
    if (theTrade && Module1Module.account && Module1Module.account.address === theTrade.value.proposal.proposer) {
      this.modalShowCancel = true;
    } else {
      this.modalShowCancel = false;
    }

    // mutually exclusive
    this.modalShowAccept = !this.modalShowCancel;
  }

  async cancelTrade () : Promise<void> {
    this.modalShowCancel = false;
    this.modalShowAccept = false;
    this.allowModalToBeClosed = false;

    try {
      if (Module1Module.xtznftswapContract && Module1Module.account) {
        const batch = await Module1Module.tezosToolkitInstance.wallet.batch();

        // call cancel cancel_trade_proposal
        batch.withContractCall(
          Module1Module.xtznftswapContract.methods.cancel_trade_proposal(
            this.tradeIdToInteractWith
          )
        );

        await Module1Module.FetchAllContractTradeData();
        const user1Tokens = Module1Module.allContractTradeData[this.tradeIdToInteractWith].value.proposal.tokens1;

        // call remove operators for each token in the trade
        for (let i = 0; i < user1Tokens.length; i++) {
          const token = user1Tokens[i];
          const fa2Contract = await Module1Module.tezosToolkitInstance.wallet.at(token.fa2);
          batch.withContractCall(
            fa2Contract.methods.update_operators([{
              remove_operator: {
                owner: Module1Module.account.address,
                operator: Module1Module.xtznftswapContract.address,
                token_id: token.id
              }
            }])
          );
        }

        this.showModal = true;
        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Waiting for you to accept the transaction in your wallet...';

        const batchOp = await batch.send();

        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Confirming trade cancellation on the blockchain...';

        await batchOp.confirmation();

        this.modalTitle = 'Success!';
        this.modalParagraph = 'You have cancelled trade #' + this.tradeIdToInteractWith;
        this.allowModalToBeClosed = true;
      }
    } catch (error: any) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;
      this.allowModalToBeClosed = true;

      // reset the UX if they just didn't go through with it
      if (error.title === 'Aborted') {
        this.openModal('' + this.tradeIdToInteractWith);
      }

      console.warn(error);
    }
  }

  async acceptTrade () : Promise<void> {
    this.modalShowCancel = false;
    this.modalShowAccept = false;
    this.allowModalToBeClosed = false;

    const theTrade = this.arrayOfDataObjects.find((datum: TradeFromTzstats) => datum.key === '' + this.tradeIdToInteractWith);

    try {
      if (!theTrade) {
        const err = new Error('Trade DNE');
        Object.assign(err, {
          title: 'Does Not Exist',
          description: 'There is no trade with that ID. If this seems like a mistake, let us know.'
        });
        throw err;
      }

      if (Module1Module.xtznftswapContract && Module1Module.account) {
        const batch = await Module1Module.tezosToolkitInstance.wallet.batch();

        // make sure contract trade data is up to date
        await this.$store.dispatch('module1/fetchAllContractTradeData');

        // for all the tokens on the accepting side
        for (let i = 0; i < theTrade.value.proposal.tokens2.length; i++) {
          const token = theTrade.value.proposal.tokens2[i];
          const fa2Contract = await Module1Module
            .tezosToolkitInstance.wallet.at(token.fa2);
          // give our contract opeartor rights
          batch.withContractCall(
            fa2Contract.methods.update_operators([{
              add_operator: {
                owner: Module1Module.account.address,
                operator: Module1Module.xtznftswapContract.address,
                token_id: token.id
              }
            }])
          );
        }

        // then accept the trade
        batch.withTransfer({
          ...Module1Module.xtznftswapContract.methodsObject
            .accept_trade(this.tradeIdToInteractWith)
            .toTransferParams({
              amount: theTrade.value.proposal.mutez_amount2 / 1000000 // in tez
            })
        });

        this.showModal = true;
        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Waiting for you to accept the transaction in your wallet...';

        const batchOp = await batch.send();

        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Confirming trade cancellation on the blockchain...';

        await batchOp.confirmation();

        this.modalTitle = 'Success!';
        this.modalParagraph = 'You have cancelled trade #' + this.tradeIdToInteractWith;
        this.allowModalToBeClosed = true;
      }
    } catch (error: any) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;
      this.allowModalToBeClosed = true;

      // reset the UX if they just didn't go through with it
      if (error.title === 'Aborted') {
        this.openModal('' + this.tradeIdToInteractWith);
      }

      console.warn(error);
    }
  }

  async accept () : Promise<void> {
    if (!Module1Module.account) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">' + 'Please Login First' + '</span>';
      this.modalParagraph = 'Once logged in you can propose trades';
      this.allowModalToBeClosed = true;
      return;
    }

    const theTrade = this.arrayOfDataObjects.find((datum: TradeFromTzstats) => datum.key === '' + this.tradeIdToInteractWith);

    try {
      this.showModal = true;
      this.modalTitle = 'Please Wait';
      this.modalParagraph = 'Confirming transaction in your wallet...';

      if (theTrade && Module1Module.xtznftswapContract && Module1Module.account) {
        const batch = await Module1Module.tezosToolkitInstance.wallet.batch();

        for (let i = 0; i < theTrade.value.proposal.tokens2.length; i++) {
          const token = theTrade.value.proposal.tokens2[i];
          const fa2Contract = await Module1Module.tezosToolkitInstance.wallet.at(token.fa2);

          batch.withContractCall(
            fa2Contract.methods.update_operators([{
              add_operator: {
                owner: Module1Module.account.address,
                operator: Module1Module.xtznftswapContract.address,
                token_id: token.id
              }
            }])
          );
        }

        batch.withTransfer({
          ...Module1Module.xtznftswapContract.methodsObject
            .accept_trade(theTrade.key)
            .toTransferParams({
              amount: theTrade.value.proposal.mutez_amount2 // in tez
            })
        });

        const batchOp = await batch.send();

        this.showModal = true;
        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Confirming trade on chain...';

        // alters the batchOp object to have _operationResult when successful
        await batchOp.confirmation();

        this.modalTitle = 'Success!';
        this.modalParagraph = 'You accepted trade ID #' + this.tradeIdToInteractWith;
        this.allowModalToBeClosed = true;
      }
    } catch (error: any) {
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;
      this.allowModalToBeClosed = true;
      console.warn(error);
    }
  }
}
